import {
  authMethodsResponseSchema,
  authSessionResponseSchema,
  logoutResponseSchema,
} from '@sivan/shared';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { ApiError, apiGet, apiPost, onSignedOut, setCsrfToken } from '../api/client';
import logo from '../assets/sivan-logo.png';
import { forgetAllDrafts, hasKeptDrafts, setDraftUser } from '../handover/draft-store';
import { forgetWorkspace } from '../master-data/use-master-data';
import { Icon } from '../ui/Icon';
import { PasswordSignIn } from './PasswordSignIn';

/** The screen (URL hash) to return to after signing in again; ids only, kept in this tab. */
const RESUME_KEY = 'sivan:resume-screen';
function rememberScreen(): void {
  try {
    if (window.location.hash.length > 1)
      window.sessionStorage.setItem(RESUME_KEY, window.location.hash);
  } catch {
    // Without storage the user returns to the start screen.
  }
}
function resumeScreen(): void {
  try {
    const hash = window.sessionStorage.getItem(RESUME_KEY);
    window.sessionStorage.removeItem(RESUME_KEY);
    if (hash !== null && /^#[A-Za-z0-9/_-]+$/.test(hash) && window.location.hash.length <= 1)
      window.location.hash = hash;
  } catch {
    // Nothing to resume.
  }
}

// Sign-in with the organization's identity provider (AUTH-1), or with the application's own form in
// the hosted deployment (GET /auth/methods answers). The server decides everything: this component
// only shows the sign-in screen when there is no session, and keeps the CSRF token in memory. With
// the development identity (no sign-in routes), the application runs as before.

/** How this deployment signs in: its own form (Supabase) or the identity provider (404). */
const signInMethod = (): Promise<'oidc' | 'password'> =>
  apiGet('/auth/methods', authMethodsResponseSchema).then(
    () => 'password',
    (error: unknown) => {
      if (error instanceof ApiError && error.status === 404) return 'oidc';
      throw error;
    },
  );

export type AuthState =
  | { readonly kind: 'checking' }
  | { readonly kind: 'development' }
  | { readonly kind: 'signed-in' }
  | {
      readonly kind: 'signed-out';
      readonly reason: 'none' | 'expired';
      /** The organization's identity provider (redirect), or the application's own form. */
      readonly method: 'oidc' | 'password';
    }
  | { readonly kind: 'unavailable' };

const MESSAGES: Readonly<Record<string, string>> = {
  failed: 'הכניסה לא הושלמה. נסו שוב.',
  denied: 'אין לחשבון זה גישה למערכת. לבירור פנו למנהלי המערכת.',
};

function SignIn({ reason }: { readonly reason: 'none' | 'expired' }) {
  const result = new URLSearchParams(window.location.search).get('signin');
  const message =
    reason === 'expired'
      ? 'פג תוקף ההתחברות. יש להיכנס שוב.'
      : result === null
        ? null
        : (MESSAGES[result] ?? null);
  const returnTo = window.location.pathname === '/' ? '/' : window.location.pathname;
  const kept = reason === 'expired' && hasKeptDrafts();
  return (
    <main className="sign-in" id="main-content">
      <div className="sign-in__card">
        <img className="sign-in__logo" src={logo} alt="סיון ביצוע" width={670} height={160} />
        <h1>{reason === 'expired' ? 'פג תוקף ההתחברות' : 'כניסה למערכת'}</h1>
        <p className="sign-in__lead">
          הכניסה מתבצעת דרך שירות הזהות של הארגון. ההרשאות נקבעות במערכת.
        </p>
        {message !== null && (
          <p className="notice notice--error" role="alert">
            {message}
          </p>
        )}
        {kept && (
          <p className="notice notice--warning" role="status">
            השינויים שלא נשמרו שמורים בדפדפן זה. התחברו מחדש כדי להמשיך מאותה נקודה.
          </p>
        )}
        <a
          className="button button--primary sign-in__button"
          href={`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`}
          onClick={rememberScreen}
        >
          {reason === 'expired' ? 'התחברות מחדש' : 'כניסה'}
        </a>
        <p className="sign-in__foot">
          <Icon name="lock" />
          הסיסמה נבדקת בשירות הזהות של הארגון בלבד
        </p>
      </div>
    </main>
  );
}

/** Signs out on the server, then follows the identity provider's sign-out. */
export async function signOut(): Promise<void> {
  // Signing out on purpose leaves nothing behind in this tab.
  forgetAllDrafts();
  forgetWorkspace();
  try {
    const { redirectUrl } = await apiPost('/auth/logout', {}, logoutResponseSchema);
    setCsrfToken(null);
    window.location.assign(redirectUrl);
  } catch {
    setCsrfToken(null);
    window.location.assign('/');
  }
}

export function AuthGate({ children }: { readonly children: (state: AuthState) => ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: 'checking' });
  const check = useCallback(() => {
    apiGet('/auth/session', authSessionResponseSchema).then(
      (session) => {
        setCsrfToken(session.csrfToken);
        setDraftUser(session.userId);
        resumeScreen();
        setState({ kind: 'signed-in' });
      },
      (error: unknown) => {
        setCsrfToken(null);
        if (error instanceof ApiError && error.status === 401)
          signInMethod().then(
            (method) => {
              setState({ kind: 'signed-out', reason: 'none', method });
            },
            () => {
              setState({ kind: 'unavailable' });
            },
          );
        // No sign-in routes: the synthetic development identity (DR-124).
        else if (error instanceof ApiError && error.status === 404)
          setState({ kind: 'development' });
        else setState({ kind: 'unavailable' });
      },
    );
  }, []);
  useEffect(check, [check]);
  useEffect(
    () =>
      onSignedOut(() => {
        setCsrfToken(null);
        signInMethod().then(
          (method) => {
            setState({ kind: 'signed-out', reason: 'expired', method });
          },
          () => {
            setState({ kind: 'unavailable' });
          },
        );
      }),
    [],
  );
  if (state.kind === 'checking')
    return (
      <div className="skeleton" role="status" aria-label="בודק התחברות">
        <div className="skeleton__row" />
      </div>
    );
  if (state.kind === 'signed-out')
    return state.method === 'password' ? (
      <PasswordSignIn
        reason={state.reason}
        keptDrafts={state.reason === 'expired' && hasKeptDrafts()}
        onSignedIn={() => {
          setState({ kind: 'checking' });
          check();
        }}
      />
    ) : (
      <SignIn reason={state.reason} />
    );
  if (state.kind === 'unavailable')
    return (
      <main className="sign-in" id="main-content">
        <div className="sign-in__card">
          <h1>השירות אינו זמין</h1>
          <p role="alert">לא ניתן להתחבר לשרת כרגע.</p>
          <button type="button" className="button" onClick={check}>
            נסו שוב
          </button>
        </div>
      </main>
    );
  return <>{children(state)}</>;
}
